/*
 * B2B line price preview.
 *
 * Shows wholesale customers the price they will actually be charged, using the
 * live BSS B2B custom pricing rules instead of a copy kept in theme settings.
 * The BSS discount is applied at checkout and never reaches Liquid's `cart`
 * object, so without this the cart lines read at full price.
 *
 * WHY THIS RUNS SYNCHRONOUSLY
 * Earlier attempts at this preview loaded deferred and produced a visible
 * price flash (full price painted first, wholesale price a beat later). BSS
 * inlines its rules into the document head, so this script is loaded WITHOUT
 * `defer` and each price node is patched by an inline call placed immediately
 * after it — the rewrite therefore happens during parsing, before the browser
 * paints that node. Do not add `defer`/`async` to the tag that loads this file.
 *
 * Fail safe: anything unexpected (missing rules, an unsupported discount type,
 * a parse error) leaves the server-rendered price exactly as Liquid wrote it.
 * A correct full price beats a wrong discounted one.
 *
 * THIS IS A PREVIEW, NOT A PRICE. Everything below runs on the shopper's own
 * machine and can be rewritten from a console, so nothing here is binding: the
 * amount charged is decided by the BSS discount at checkout, against the real
 * server-side customer record. Never move an eligibility or discount decision
 * into this file expecting it to hold. See the trust boundary note in
 * assets/b2b-session.js.
 */
(function () {
  'use strict';

  var DISCOUNT_TYPE_PERCENT = 2;
  var PROCESSED = 'b2bPriceApplied';

  function config() {
    return window.themeB2BConfig || null;
  }

  function sessionTags() {
    var session = window.themeB2BSession;
    return session ? session.tags : [];
  }

  /*
   * Every node this script has rewritten, mapped to the exact markup Liquid
   * produced for it. Signing out has to be as reversible as signing in — a
   * session can change under a document that is already priced, and leaving a
   * wholesale price on screen for a shopper who no longer earns it is a worse
   * bug than the one this file exists to fix. A WeakMap keeps replaced nodes
   * collectable when a section re-renders over them.
   */
  var originalMarkup = new WeakMap();

  /* ---------------------------------------------------------------- money */

  function formatMoney(cents, format) {
    var value = Math.round(Number(cents) || 0);
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    var match = format.match(placeholder);
    var name = match ? match[1] : 'amount';

    function withDelimiters(precision, thousands, decimal) {
      var parts = (value / 100.0).toFixed(precision).split('.');
      var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return whole + (parts[1] ? decimal + parts[1] : '');
    }

    var formatted;
    switch (name) {
      case 'amount_no_decimals':
        formatted = withDelimiters(0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        formatted = withDelimiters(2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        formatted = withDelimiters(0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        formatted = withDelimiters(2, "'", '.');
        break;
      default:
        formatted = withDelimiters(2, ',', '.');
    }
    return format.replace(placeholder, formatted);
  }

  function money(cents) {
    var settings = config();
    var format = settings.useCurrencyCode
      ? settings.moneyWithCurrencyFormat
      : settings.moneyFormat;
    return formatMoney(cents, format || '${{amount}}');
  }

  /* ---------------------------------------------------------------- rules */

  function tagsMatch(ruleTags, customerTags) {
    if (!ruleTags) return false;
    return String(ruleTags)
      .split(',')
      .some(function (tag) {
        var needle = tag.trim().toLowerCase();
        return needle !== '' && customerTags.indexOf(needle) !== -1;
      });
  }

  var cachedRules = null;

  /**
   * Custom pricing rules. `configData` is the raw array BSS inlines server
   * side, so it is present before any of its own bundles execute; `cp.cpRules`
   * is the same data once the app has booted.
   */
  function readRules() {
    if (cachedRules) return cachedRules;

    var root = window.BSS_B2B || {};
    var raw =
      (Array.isArray(root.configData) && root.configData) ||
      (root.cp && Array.isArray(root.cp.cpRules) && root.cp.cpRules) ||
      (window.BSS_B2B_CLONE && Array.isArray(window.BSS_B2B_CLONE.configData) && window.BSS_B2B_CLONE.configData) ||
      [];

    var customerTags = sessionTags();

    cachedRules = raw
      .filter(function (rule) {
        // Skip rules with exclusions: the theme cannot model them, and a
        // preview that ignores an exclusion would overstate the discount.
        if (Number(rule.xpct) !== 0) return false;
        if (Number(rule.dt) !== DISCOUNT_TYPE_PERCENT) return false;
        return tagsMatch(rule.ct, customerTags);
      })
      .map(function (rule) {
        var collections = Object.create(null);
        String(rule.pc || '')
          .split(',')
          .forEach(function (id) {
            var trimmed = id.trim();
            if (trimmed) collections[trimmed] = true;
          });
        return { name: rule.n, percent: parseFloat(rule.dv), collections: collections };
      })
      .filter(function (rule) {
        return isFinite(rule.percent) && rule.percent > 0 && rule.percent < 100;
      });

    return cachedRules;
  }

  function matchRule(collectionIds) {
    var rules = readRules();
    for (var i = 0; i < rules.length; i++) {
      for (var j = 0; j < collectionIds.length; j++) {
        if (rules[i].collections[collectionIds[j]]) return rules[i];
      }
    }
    return null;
  }

  /* --------------------------------------------------------------- render */

  /**
   * Built with DOM calls rather than an HTML string on purpose. Two of the
   * inputs here — the strike class and the money format — are read back out of
   * the document at runtime, so concatenating them into markup would turn any
   * future code path that can write `data-b2b-strike-class` (an app block, a
   * metafield, another developer's snippet) into script injection. `textContent`
   * and `setAttribute` cannot execute anything, whatever ends up in them.
   */
  function markup(originalCents, discountedCents, strikeClass) {
    var fragment = document.createDocumentFragment();

    var original = document.createElement('s');
    original.setAttribute('class', strikeClass);
    original.textContent = money(originalCents);

    var sale = document.createElement('span');
    sale.setAttribute(
      'class',
      'selection:bg-text-[rgb(var(--colors-price-sale),0.2)] price-sale'
    );
    sale.textContent = money(discountedCents);

    fragment.appendChild(original);
    fragment.appendChild(sale);
    return fragment;
  }

  function applyToNode(node) {
    if (!node || node.nodeType !== 1) return;
    if (node.dataset[PROCESSED] === '1') return;

    if (!config() || !sessionTags().length) return;

    var amount = Number(node.getAttribute('data-b2b-amount'));
    if (!isFinite(amount) || amount <= 0) return;

    var collectionIds = (node.getAttribute('data-b2b-collections') || '')
      .split(',')
      .filter(Boolean);
    if (!collectionIds.length) return;

    var rule = matchRule(collectionIds);
    if (!rule) return;

    var discounted = Math.round((amount * (100 - rule.percent)) / 100);
    if (discounted >= amount) return;

    // Keep the server-rendered nodes themselves, not their serialized HTML:
    // restoring them later is then a move, never a re-parse of a string.
    if (!originalMarkup.has(node)) {
      var kept = document.createDocumentFragment();
      while (node.firstChild) kept.appendChild(node.firstChild);
      originalMarkup.set(node, kept);
    }

    // Single atomic write: never blank the node out first.
    node.replaceChildren(
      markup(
        amount,
        discounted,
        node.getAttribute('data-b2b-strike-class') || 'cap mr-1 inline-block opacity-70'
      )
    );
    node.dataset[PROCESSED] = '1';
  }

  function apply(root) {
    try {
      if (!root) return;
      if (root.nodeType === 1 && root.hasAttribute('data-b2b-amount')) {
        applyToNode(root);
        return;
      }
      (root.querySelectorAll ? root.querySelectorAll('[data-b2b-amount]') : []).forEach(applyToNode);
    } catch (error) {
      /* Leave the Liquid-rendered price untouched. */
    }
  }

  /*
   * The cart drawer and cart page re-render over AJAX, replacing these nodes.
   * A repaint there is not a first-paint flash, so an observer is enough.
   */
  function watch() {
    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        Array.prototype.forEach.call(mutation.addedNodes, function (node) {
          if (node.nodeType === 1) apply(node);
        });
      });
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
  }

  /**
   * Put every rewritten price back exactly as Liquid wrote it, and forget the
   * rules derived from the old tags. Called before re-pricing under a new
   * session, so a sign-out leaves no wholesale price behind and a change of
   * wholesale tier never compounds one discount on top of another.
   */
  function reset() {
    document.querySelectorAll('[data-b2b-amount]').forEach(function (node) {
      if (node.dataset[PROCESSED] !== '1') return;
      if (originalMarkup.has(node)) {
        // Moves the kept nodes back in, which empties the stored fragment —
        // so drop the entry too, or a later re-apply would keep the spent
        // fragment and the next reset would blank the price out entirely.
        node.replaceChildren(originalMarkup.get(node));
        originalMarkup.delete(node);
      }
      delete node.dataset[PROCESSED];
    });
    cachedRules = null;
  }

  function watchSession() {
    if (!window.themeB2BSession) return;
    window.themeB2BSession.onChange(function () {
      reset();
      apply(document.documentElement);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      watch();
      watchSession();
    });
  } else {
    watch();
    watchSession();
  }

  window.themeB2BPrices = {
    apply: apply,
    reset: reset,
    readRules: readRules,
    formatMoney: formatMoney
  };
})();
